import React, { useState, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Field } from 'formik';

import Accordion from 'apollo-react/components/Accordion';
import AccordionDetails from 'apollo-react/components/AccordionDetails';
import AccordionSummary from 'apollo-react/components/AccordionSummary';
import Typography from '../../common/StyledTypography';
import Grid from 'apollo-react/components/Grid';
import Card from 'apollo-react/components/Card';
import CardContent from 'apollo-react/components/CardContent';
import CardHeader from 'apollo-react/components/CardHeader';
import Button from 'apollo-react/components/Button';
import CircularProgress from 'apollo-react/components/CircularProgress';

import StatusCheck from 'apollo-react-icons/StatusCheck';
import ArrowDown from 'apollo-react-icons/ArrowDown';
import ArrowUp from 'apollo-react-icons/ArrowUp';
import CloseCircle from 'apollo-react-icons/CloseCircle';
import CheckboxInput from '../../common/Form/CheckboxInput';
import { COLORS, FONTS, SPACING } from '../../../constants/styleConstants';
import { isReadLockedInSection } from '../../../hooks/utils/privilegesUtils';

// Styles object for better organization and reusability
const styles = {
  // Accordion Styles
  accordion: {
    marginTop: SPACING.lg,
  },
  accordionSummary: {
    padding: `${SPACING.sm} 12px ${SPACING.sm} ${SPACING.sm}`,
    backgroundColor: COLORS.lightGray,
    border: 'none',
  },
  accordionDetails: {
    marginTop: SPACING.lg,
    padding: '0px',
    gap: SPACING.lg,
  },

  // Layout Styles
  gridItem: {
    // padding: `0 0 0 20px`,
    // marginTop: '9px',
  },

  // Card Styles
  card: {
    width: '100%',
    borderRadius: '5px',
    padding: '0px',
    // margin: '7px 0px',
    backgroundColor: COLORS.white,
    overflow: 'hidden',
    // height: '100%',
  },
  cardHeader: {
    width: '100%',
    padding: `11px 7px 0px 7px`,
    fontSize: FONTS.sizes.medium,
    fontWeight: FONTS.weights.semiBold,
    cursor: 'pointer',
  },
  expandedCardContent: {
    width: '100%',
    padding: `${SPACING.lg} 0 10px ${SPACING.xl}`,
  },
  collapsedCardContent: {
    width: '100%',
    padding: SPACING.md,
  },

  // Card  Header Styles
  headerContent: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
  },
  headerLeft: {
    display: 'flex',
  },
  headerRight: {
    display: 'flex',
    alignItems: 'center',
  },
  sectionTitle: {
    marginLeft: SPACING.sm,
    fontSize: FONTS.sizes.medium,
    color: COLORS.darkGray,
  },

  // Icon Styles
  statusIcon: {
    color: COLORS.lightBlue,
    fontSize: FONTS.sizes.medium,
  },
  arrowIcon: {
    marginRight: '7px',
    color: COLORS.mediumGray,
    fontSize: FONTS.sizes.large,
  },

  // Button Styles
  clearAllButton: {
    fontSize: FONTS.sizes.small,
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    minWidth: '0',
    cursor: 'pointer',
    color: COLORS.mediumGray,
    textDecoration: 'underline',
    fontWeight: FONTS.weights.normal,
  },
  selectAllButton: {
    width: '157px',
    height: '40px',
  },

  privilegeButton: {
    color: COLORS.blue,
    border: `1px solid ${COLORS.gray}`,
    borderRadius: '10px',
    fontSize: FONTS.sizes.small,
    fontWeight: FONTS.weights.medium,
    lineHeight: '21px',
    letterSpacing: '0.28px',
    flex: '0 0 calc(50% - 2px)', // 2 buttons per row with gap spacing
    maxWidth: 'calc(50% - 2px)',
    cursor: 'default',
    backgroundColor: 'transparent',
  },
  allPrivilegeButton: {
    color: COLORS.blue,
    border: `1px solid ${COLORS.gray}`,
    borderRadius: '10px',
    fontSize: FONTS.sizes.small,
    fontWeight: FONTS.weights.medium,
    lineHeight: '21px',
    letterSpacing: '0.28px',
    width: '100%',
  },

  privilegeContainer: {
    marginTop: SPACING.sm,
    display: 'flex',
    flexWrap: 'wrap',
    gap: SPACING.xs,
  },
  checkboxStyle: {
    padding: 0,
  },
  checkboxContainerLabelStyle: {
    fontWeight: FONTS.weights.normal,
  },
  readOnlyPrivilegeItem: {
    padding: '4px 0',
  },

  // Category Header Styles
  categoryHeader: {
    fontSize: FONTS.sizes.large,
    fontWeight: FONTS.weights.bold,
    color: COLORS.darkGray,
    // marginTop: SPACING.xl,
    // marginBottom: SPACING.md,
    // padding: `${SPACING.sm} 0`,
  },
  categorySection: {
    // marginBottom: SPACING.xl,
  },
};

// Helper functions for better code organization
const formatPrivilegeName = (name, capitalizeFirstLetter, t) => {
  // if (name === 'read') {
  //   return t('read_only');
  // }

  // Try multiple variations to find translation
  const lowerName = name.toLowerCase();
  const dashedName = lowerName.replace(/\s+/g, '-');
  const spacedName = lowerName.replace(/[-_]/g, ' ');

  const translation = t(lowerName) || t(dashedName) || t(spacedName);

  return translation
    ? capitalizeFirstLetter(translation)
    : capitalizeFirstLetter(name);
};

const getSelectedPrivileges = (value, privilegeIndexMap, boxesState) => {
  return value.filter((priv) => {
    const globalIndex = privilegeIndexMap.get(priv.id);
    return globalIndex !== undefined && boxesState[globalIndex];
  });
};

const buildCollapsedCardsState = (sections) => {
  const state = {};
  if (!sections || Object.keys(sections).length === 0) return state;
  Object.entries(sections).forEach(([category, resources]) => {
    Object.keys(resources).forEach((resource) => {
      const resourceKey = `${category}-${resource}`;
      state[resourceKey] = false;
    });
  });
  return state;
};

// Helper function to check if all privileges in a single section are selected
// const areAllPrivilegesSelected = (value, privileges, boxesState) => {
//   return value.every((priv) => {
//     const globalIndex = privileges.findIndex((p) => p.id === priv.id);
//     return boxesState[globalIndex];
//   });
// };

// Card Header Component
const CardHeaderContent = ({
  category,
  resource,
  expandedCards,
  handleSectionClearAll,
  capitalizeFirstLetter,
  t,
  readOnly,
  hasChecked,
}) => {
  const resourceKey = `${category}-${resource}`;
  return (
    <div style={styles.headerContent}>
      <div style={styles.headerLeft}>
        {hasChecked && <StatusCheck style={styles.statusIcon} />}
        <span style={styles.sectionTitle}>
          {(() => {
            // Try multiple variations to find translation for resource names
            const lowerResource = resource.toLowerCase();
            let translation;

            if (lowerResource === 'profile') {
              // For profile, try profile_resource first to avoid object conflict
              translation = t('profile_resource');
            } else {
              // For other resources, try the standard variations
              const dashedResource = lowerResource.replace(/\s+/g, '-');
              const spacedResource = lowerResource.replace(/[-_]/g, ' ');
              translation =
                t(lowerResource) || t(dashedResource) || t(spacedResource);
            }

            return translation
              ? capitalizeFirstLetter(translation)
              : capitalizeFirstLetter(resource);
          })()}
        </span>
      </div>
      <div style={styles.headerRight}>
        {expandedCards[resourceKey] ? (
          <ArrowUp style={styles.arrowIcon} />
        ) : (
          <ArrowDown style={styles.arrowIcon} />
        )}
        {!readOnly && (
          <div
            onClick={(e) => {
              e.stopPropagation();
              handleSectionClearAll(category, resource);
            }}
            style={styles.clearAllButton}
          >
            {t('clear_all')}
          </div>
        )}
      </div>
    </div>
  );
};

// Privilege Buttons Component
const PrivilegeButtons = ({
  value,
  privilegeIndexMap,
  boxesState,
  capitalizeFirstLetter,
  t,
  handleCheckboxChange,
  readOnly,
}) => {
  const selectedPrivileges = getSelectedPrivileges(
    value,
    privilegeIndexMap,
    boxesState
  );
  const readLocked = isReadLockedInSection(
    value,
    privilegeIndexMap,
    boxesState
  );

  return (
    <div style={styles.privilegeContainer}>
      {selectedPrivileges.map((priv) => {
        let privilegeName = formatPrivilegeName(
          priv?.name,
          capitalizeFirstLetter,
          t
        );

        if (privilegeName.length > 10) {
          privilegeName =
            privilegeName.charAt(9) === ' '
              ? `${privilegeName.substring(0, 9)}...`
              : `${privilegeName.substring(0, 10)}...`;
        }

        const globalIndex = privilegeIndexMap.get(priv.id);
        const hideRemove = readOnly || (priv.name === 'read' && readLocked);
        return (
          <Button
            key={priv.id}
            variant="secondary"
            icon={
              hideRemove ? undefined : (
                <CloseCircle
                  onClick={() => handleCheckboxChange(priv, globalIndex)}
                  style={{ cursor: 'pointer' }}
                />
              )
            }
            size="small"
            style={styles.privilegeButton}
          >
            {privilegeName}
          </Button>
        );
      })}
    </div>
  );
};

// Privilege Checkboxes Component
const PrivilegeCheckboxes = ({
  value,
  privilegeIndexMap,
  boxesState,
  handleCheckboxChange,
  capitalizeFirstLetter,
  t,
}) => {
  const readLocked = isReadLockedInSection(
    value,
    privilegeIndexMap,
    boxesState
  );

  return (
    <Grid container sx={{ width: '100%' }}>
      {Array.isArray(value) &&
        value.map((priv) => {
          const globalIndex = privilegeIndexMap.get(priv.id);
          const isDisabled = priv.name === 'read' && readLocked;
          return (
            <Grid
              key={priv.id}
              size={12}
              onClick={(event) => event.stopPropagation()}
            >
              <Field name={priv.id}>
                {({ field, meta }) => (
                  <CheckboxInput
                    {...field}
                    meta={meta}
                    label={
                      <div style={styles.checkboxContainerLabelStyle}>
                        {formatPrivilegeName(
                          priv?.name,
                          capitalizeFirstLetter,
                          t
                        )}
                      </div>
                    }
                    value={parseInt(priv.id)}
                    checked={boxesState[globalIndex]}
                    onChange={() => handleCheckboxChange(priv, globalIndex)}
                    disabled={isDisabled}
                    style={styles.checkboxStyle}
                  />
                )}
              </Field>
            </Grid>
          );
        })}
    </Grid>
  );
};

const PrivilegesSections = ({
  sections,
  isSectionSelected,
  published,
  privileges,
  boxesState,
  handleSectionClearAll,
  handleSectionSelection,
  handleCheckboxChange,
  capitalizeFirstLetter,
  selectedPrivileges,
  handleSelectAll,
  onReset,
  readOnly = false,
  collapseRevision = 0,
}) => {
  const [expandedCards, setExpandedCards] = useState({});
  const { t, ready } = useTranslation();

  const privilegeIndexMap = useMemo(() => {
    const map = new Map();
    privileges.forEach((p, index) => map.set(p.id, index));
    return map;
  }, [privileges]);

  useEffect(() => {
    if (sections && Object.keys(sections).length > 0) {
      setExpandedCards(buildCollapsedCardsState(sections));
    }
  }, [sections]);

  useEffect(() => {
    if (collapseRevision > 0 && sections && Object.keys(sections).length > 0) {
      setExpandedCards(buildCollapsedCardsState(sections));
    }
  }, [collapseRevision, sections]);

  const toggleCardExpansion = (category, resource) => {
    const resourceKey = `${category}-${resource}`;
    setExpandedCards((prev) => ({
      ...prev,
      [resourceKey]: !prev[resourceKey],
    }));
  };

  const renderExpandedContent = (category, resource, value) => {
    const resourceKey = `${category}-${resource}`;

    if (readOnly) {
      return (
        <CardContent style={styles.expandedCardContent}>
          <Grid container sx={{ width: '100%' }}>
            {Array.isArray(value) &&
              value.map((priv) => (
                <Grid
                  key={priv.id}
                  size={12}
                  style={styles.readOnlyPrivilegeItem}
                >
                  <span style={styles.checkboxContainerLabelStyle}>
                    {formatPrivilegeName(priv?.name, capitalizeFirstLetter, t)}
                  </span>
                </Grid>
              ))}
          </Grid>
        </CardContent>
      );
    }

    return (
      <CardContent style={styles.expandedCardContent}>
        <Field name="select_all">
          {({ field, meta }) => (
            <CheckboxInput
              {...field}
              meta={meta}
              label={
                <div style={styles.checkboxContainerLabelStyle}>
                  {t('select_all')}
                </div>
              }
              checked={isSectionSelected[resourceKey]}
              value={published}
              onChange={() => {
                handleSectionSelection(category, resource);
              }}
              style={styles.checkboxStyle}
            />
          )}
        </Field>
        <PrivilegeCheckboxes
          value={value}
          privilegeIndexMap={privilegeIndexMap}
          boxesState={boxesState}
          handleCheckboxChange={handleCheckboxChange}
          capitalizeFirstLetter={capitalizeFirstLetter}
          t={t}
        />
      </CardContent>
    );
  };

  const renderCollapsedContent = (value) => (
    <CardContent style={styles.collapsedCardContent}>
      {Array.isArray(value) && value.length > 0 && (
        <PrivilegeButtons
          value={value}
          privilegeIndexMap={privilegeIndexMap}
          boxesState={boxesState}
          capitalizeFirstLetter={capitalizeFirstLetter}
          t={t}
          handleCheckboxChange={handleCheckboxChange}
          readOnly={readOnly}
        />
      )}
    </CardContent>
  );

  // Don't render until translations are ready
  if (!ready) {
    return (
      <Accordion defaultExpanded size="small" style={styles.accordion}>
        <AccordionSummary style={styles.accordionSummary}>
          <div style={{ display: 'flex', justifyContent: 'center' }}>
            <CircularProgress />
          </div>
        </AccordionSummary>
      </Accordion>
    );
  }

  return (
    <Accordion defaultExpanded size="small" style={styles.accordion}>
      <AccordionSummary style={styles.accordionSummary}>
        <Typography>{t('privilegesSection.title')}</Typography>
      </AccordionSummary>
      <AccordionDetails style={styles.accordionDetails}>
        {!readOnly && sections && Object.entries(sections || {}).length > 0 && (
          <div style={{ display: 'flex', gap: '8px' }}>
            <Button
              variant="primary"
              onClick={handleSelectAll}
              style={styles.selectAllButton}
            >
              {selectedPrivileges.length === privileges.length
                ? t('unselect_all')
                : t('select_all')}
            </Button>
            {onReset && (
              <Button
                variant="secondary"
                onClick={onReset}
                style={styles.selectAllButton}
              >
                {t('privilegesSection.resetToRole')}
              </Button>
            )}
          </div>
        )}
        {sections &&
          Object.entries(sections || {}).map(([category, resources]) => (
            <div key={category} style={styles.categorySection}>
              <Typography variant="title" style={styles.categoryHeader}>
                {(() => {
                  // Try multiple variations to find translation
                  const lowerCategory = category.toLowerCase();
                  let categoryTranslation;

                  if (lowerCategory === 'profile') {
                    // For profile, try profile_resource first to avoid object conflict
                    categoryTranslation = t('profile_resource');
                  } else {
                    // For other categories, try the standard variations
                    const dashedCategory = lowerCategory.replace(/\s+/g, '-');
                    const spacedCategory = lowerCategory.replace(/[-_]/g, ' ');
                    categoryTranslation =
                      t(lowerCategory) ||
                      t(dashedCategory) ||
                      t(spacedCategory);
                  }

                  return categoryTranslation || category;
                })()}
              </Typography>
              <Grid container spacing={1} sx={{ width: '100%' }}>
                {Object.entries(resources).map(
                  ([resource, resourcePrivileges]) => {
                    const resourceKey = `${category}-${resource}`;
                    const hasChecked = resourcePrivileges.some((priv) => {
                      const globalIndex = privilegeIndexMap.get(priv.id);
                      return (
                        globalIndex !== undefined && boxesState[globalIndex]
                      );
                    });
                    return (
                      <Grid
                        size={{ xs: 12, md: 4 }}
                        key={resourceKey}
                        style={styles.gridItem}
                      >
                        <Card tabIndex={0} interactive style={styles.card}>
                          <CardHeader
                            style={styles.cardHeader}
                            onClick={() =>
                              toggleCardExpansion(category, resource)
                            }
                            title={
                              <CardHeaderContent
                                category={category}
                                resource={resource}
                                expandedCards={expandedCards}
                                handleSectionClearAll={handleSectionClearAll}
                                capitalizeFirstLetter={capitalizeFirstLetter}
                                t={t}
                                readOnly={readOnly}
                                hasChecked={hasChecked}
                              />
                            }
                          />

                          {expandedCards[resourceKey]
                            ? renderExpandedContent(
                                category,
                                resource,
                                resourcePrivileges
                              )
                            : renderCollapsedContent(resourcePrivileges)}
                        </Card>
                      </Grid>
                    );
                  }
                )}
              </Grid>
            </div>
          ))}
      </AccordionDetails>
    </Accordion>
  );
};

export default PrivilegesSections;
